import { MenuUnfoldOutlined } from "@ant-design/icons";
import { Grid, Layout } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import Contentbar from "../components/main/Contentbar";
import Headerbar from "../components/main/Headerbar";
import Sidebar from "../components/main/Sidebar";
import { OTP_MODAL_EXCLUDE_PATHS } from "./utils/commonUtils";
import { useSearchParams } from "react-router-dom";
const AppLayout = ({ setLoading, breadCrumbItems, setBreadCrumbItems, accessDenied, setAccessDenied }) => {
  const [headerTitle, setHeaderTitle] = useState("");
  const [serviceName, setServiceName] = useState("");
  const { lg } = Grid.useBreakpoint();

  const [searchParams] = useSearchParams();
  let sideBar = searchParams.get("sideBar");
  if (navigator.userAgent.includes("HelloSendMobile")) {
    sideBar = "false";
  }
  const [isPinned, setIsPinned] = useState(() => {
    // Get pinned state from localStorage
    const pinnedState = localStorage.getItem("sideBarPinned");
    const isDesktopView = window.innerWidth >= 992; // Assuming 992px as the breakpoint for desktop
    return pinnedState !== null ? JSON.parse(pinnedState) : isDesktopView;
  });

  const [sideBarAbsent, setSideBarAbsent] = useState(() => {
    // Get saved state from localStorage, default to true if not found
    const savedState = localStorage.getItem("sideBarHidden");
    const isPinnedValue = localStorage.getItem("sideBarPinned") === "true";

    if (sideBar === "false") {
      return true;
    }

    // If pinned, always show sidebar (not absent)
    if (isPinnedValue) {
      return false;
    }

    return savedState !== null ? JSON.parse(savedState) : true;
  });

  const [isFloating, setIsFloating] = useState(false);
  let path = window.location.pathname;

  const toggleSidebar = () => {
    if (sideBarAbsent) {
      // Opening the sidebar
      setSideBarAbsent(false);

      // If not pinned, set to floating mode
      if (!isPinned) {
        setIsFloating(true);
      } else {
        // Store state in localStorage only when pinned
        localStorage.setItem("sideBarHidden", JSON.stringify(false));
      }
    } else {
      // Closing the sidebar {
      if (isPinned) {
        if (!lg) {
          setIsFloating(true);
          setSideBarAbsent(true);
          setIsPinned(false);
          localStorage.setItem("sideBarHidden", JSON.stringify(true));
        } else {
          localStorage.setItem("sideBarHidden", JSON.stringify(false));
          setSideBarAbsent(false);
          setIsFloating(false);
          setIsPinned(true);
        }
      } else {
        // If not pinned, close it
        setSideBarAbsent(true);
        setIsFloating(false);
        localStorage.setItem("sideBarHidden", JSON.stringify(true));
      }
    }
  };

  const togglePin = () => {
    setIsPinned((prev) => {
      const newPinState = !prev;
      localStorage.setItem("sideBarPinned", JSON.stringify(newPinState));

      if (newPinState) {
        // When pinning, ensure sidebar is visible and save to localStorage
        setSideBarAbsent(false);
        setIsFloating(false);
        localStorage.setItem("sideBarHidden", JSON.stringify(false));
      } else {
        // When unpinning, hide the sidebar immediately unless in temporary floating mode
        setSideBarAbsent(true);
        setIsFloating(false);
        localStorage.setItem("sideBarHidden", JSON.stringify(true));
      }

      return newPinState;
    });
  };

  // Handle navigation - close sidebar if in floating mode
  const handleNavigation = () => {
    // Only close the sidebar if it's NOT pinned
    if (!isPinned) {
      setSideBarAbsent(true);
      setIsFloating(false);
    }
  };
  useEffect(() => {
    // If sidebar is pinned, always show it regardless of path (except special cases)
    const isSpecialPath =
      path === "/zoho-crm/webhook" || path === "/zoho-crm/landing" || path === "/teams/landing" || path === "/salesforce/landing";

    if (isPinned && !isSpecialPath) {
      setSideBarAbsent(false);
    } else if (
      isSpecialPath ||
      (sideBar === "false" &&
        searchParams.get("onBoardingPage") !== "true" &&
        path !== "/messages" &&
        path !== "/zoho-crm/webhook" &&
        path !== "/content" &&
        path !== "/sendsms" &&
        !OTP_MODAL_EXCLUDE_PATHS.includes(path))
    ) {
      setSideBarAbsent(false);
    } else if (sideBar === "false" && (path === "/sendsms" || path === "/messages" || path === "/content")) {
      // Only hide if not pinned
      if (!isPinned) {
        setSideBarAbsent(true);
      }
    }
  }, [path, sideBar, isPinned]);

  const HEADER_BAR_HEIGHT = 64;
  const headerBarRef = useRef(null);

  return (
    <Layout>
      {!sideBarAbsent && (
        <div
          className={`sidebar-overlay ${sideBarAbsent ? "hide-sidebar" : "show-sidebar"} ${isFloating && !isPinned ? "floating-overlay" : ""}`}
          onClick={(e) => {
            // Only close when clicking the overlay, not the sidebar itself
            if (!lg) {
              toggleSidebar();
            } else {
              if (e.target === e.currentTarget && (!isPinned || isFloating)) {
                setSideBarAbsent(true);
                setIsFloating(false);
              }
            }
          }}
        >
          <Sidebar
            toggleSidebar={toggleSidebar}
            isPinned={isPinned}
            togglePin={togglePin}
            handleNavigation={handleNavigation}
            isFloating={isFloating}
            setAccessDenied={setAccessDenied}
          />
        </div>
      )}

      {/* Toggle sidebar button - only visible when sidebar is hidden */}
      {sideBarAbsent && lg && (
        <div
          className="sidebar-toggle-button"
          onClick={toggleSidebar}
          style={{
            position: "fixed",
            left: "0px",
            top: "45px",
            zIndex: 1000,
            background: "white",
            padding: "8px",
            borderRadius: "50%",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MenuUnfoldOutlined style={{ fontSize: "16px" }} data-cy="sidebar-toggle-icon" />
        </div>
      )}

      <Layout className="hs-bg-off-white h-100">
        <Headerbar 
          headerBarRef={headerBarRef}
          headerBarHeight={HEADER_BAR_HEIGHT} 
          toggleSidebar={toggleSidebar} 
          headerTitle={headerTitle} 
          setServiceName={setServiceName}
        />
        <Contentbar
          headerBarHeight={headerBarRef.current ? headerBarRef.current.offsetHeight : HEADER_BAR_HEIGHT}
          breadcrumbItems={breadCrumbItems}
          setBreadCrumbItems={setBreadCrumbItems}
          serviceName={serviceName}
          setLoading={setLoading}
          setHeaderTitle={setHeaderTitle}
          accessDenied={accessDenied}
          setAccessDenied={setAccessDenied}
        />
      </Layout>
      {/* <VoiceLayout/> */}
    </Layout>
  );
};

export default AppLayout;
